import { makeStyles } from '@material-ui/core/styles';
import { claymorphismColors } from '../constants/colors';

export const useStyles = makeStyles((theme) => ({
  // Loading container - full screen centered
  loadingContainer: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 50%, #FFF3E0 100%)',
  },

  // Main container with warm gradient background
  root: {
    width: '100%',
    height: '100vh',
    background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 50%, #FFF3E0 100%)',
    overflow: 'auto',
    [theme.breakpoints.down('sm')]: {
      overflowX: 'hidden',
    },
  },

  // Content wrapper
  contentWrapper: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 24px 24px 24px',
    [theme.breakpoints.down('sm')]: {
      padding: '0 16px 16px 16px',
    },
  },

  // Base Claymorphism Card
  clayCard: {
    borderRadius: '20px',
    border: '3px solid rgba(255, 255, 255, 0.6)',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: `
      6px 6px 12px rgba(0, 0, 0, 0.08),
      -4px -4px 10px rgba(255, 255, 255, 0.8),
      inset 2px 2px 4px rgba(255, 255, 255, 0.6),
      inset -1px -1px 2px rgba(0, 0, 0, 0.03)
    `,
    transition: 'transform 0.2s ease-out, box-shadow 0.2s ease-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `
        8px 8px 16px rgba(0, 0, 0, 0.1),
        -5px -5px 12px rgba(255, 255, 255, 0.9),
        inset 2px 2px 4px rgba(255, 255, 255, 0.6),
        inset -1px -1px 2px rgba(0, 0, 0, 0.03)
      `,
    },
    [theme.breakpoints.down('sm')]: {
      borderRadius: '16px',
      padding: '16px',
      marginBottom: '16px',
      boxShadow: `
        4px 4px 8px rgba(0, 0, 0, 0.06),
        -2px -2px 6px rgba(255, 255, 255, 0.7)
      `,
    },
  },

  // Card color variants
  clayCardOrange: {
    background: `linear-gradient(145deg, ${claymorphismColors.softOrange}, #FFE8CC)`,
  },
  clayCardGold: {
    background: `linear-gradient(145deg, ${claymorphismColors.softGold}, #FFF8E1)`,
  },
  clayCardCyan: {
    background: `linear-gradient(145deg, ${claymorphismColors.softCyan}, #B2EBF2)`,
  },

  // Section title with accent bar
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
    color: claymorphismColors.text,
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    '&::before': {
      content: '""',
      width: '4px',
      height: '24px',
      borderRadius: '2px',
      marginRight: '12px',
      backgroundColor: claymorphismColors.accent,
    },
  },

  // Filter container - sticky header
  filterContainer: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: '16px 24px',
    marginBottom: '16px',
    flexWrap: 'wrap',
    background: 'linear-gradient(135deg, #FFF8E1 0%, #FFECB3 50%, #FFF3E0 100%)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
    [theme.breakpoints.down('sm')]: {
      padding: '12px 16px',
      marginBottom: '12px',
      flexDirection: 'column',
      alignItems: 'center',
    },
  },

  // Select field styling
  claySelect: {
    width: '280px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '16px',
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      boxShadow: 'inset 2px 2px 5px rgba(0, 0, 0, 0.05)',
      border: '2px solid rgba(255, 255, 255, 0.5)',
      transition: 'all 0.2s ease-out',
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: '2px',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.2)',
      },
      '&.Mui-focused fieldset': {
        borderColor: claymorphismColors.accent,
        boxShadow: `0 0 0 3px rgba(232, 93, 4, 0.2)`,
      },
    },
    '& .MuiInputLabel-root': {
      color: '#607D8B',
      fontWeight: 500,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: claymorphismColors.accent,
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  },

  // Table container
  tableContainer: {
    width: '100%',
    overflowX: 'auto',
    '& table': {
      width: '100%',
    },
  },

  // Card field styles
  cardLabel: {
    fontSize: '0.75rem',
    color: claymorphismColors.textLight,
    fontWeight: 500,
    marginBottom: '2px',
  },
  cardValue: {
    fontSize: '0.95rem',
    color: claymorphismColors.text,
    fontWeight: 400,
    marginBottom: '12px',
    wordBreak: 'break-word',
  },

  // Empty state
  emptyState: {
    textAlign: 'center',
    padding: '32px 16px',
    color: claymorphismColors.textLight,
  },
  emptyStateIcon: {
    fontSize: '3rem',
    marginBottom: '8px',
  },
  emptyStateText: {
    fontSize: '1rem',
    fontWeight: 500,
  },

  // Grouped duplicate card styles
  groupCard: {
    background: 'rgba(255, 255, 255, 0.7)',
    borderRadius: '16px',
    padding: '16px',
    marginBottom: '16px',
    border: '2px solid rgba(255, 255, 255, 0.8)',
    boxShadow: '3px 3px 8px rgba(0, 0, 0, 0.06)',
    transition: 'box-shadow 0.2s ease-out',
    '&:hover': {
      boxShadow: '4px 4px 12px rgba(0, 0, 0, 0.08)',
    },
  },

  groupCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },

  groupCardName: {
    fontSize: '1.1rem',
    fontWeight: 600,
    color: claymorphismColors.text,
    marginBottom: '4px',
  },

  groupCardBadge: {
    backgroundColor: claymorphismColors.accent,
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },

  contactRow: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '8px',
    gap: '8px',
    minWidth: 0,
  },

  contactIcon: {
    color: claymorphismColors.textLight,
    fontSize: '1.1rem',
    marginRight: '4px',
  },

  contactText: {
    fontSize: '0.9rem',
    color: claymorphismColors.text,
    marginRight: '8px',
    wordBreak: 'break-all',
    minWidth: 0,
    flex: 1,
  },

  contactButton: {
    padding: '4px 12px',
    borderRadius: '8px',
    fontSize: '0.75rem',
    fontWeight: 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease-out',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
  },

  callButton: {
    backgroundColor: '#4CAF50',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#45a049',
    },
  },

  emailButton: {
    backgroundColor: '#2196F3',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#1976D2',
    },
  },

  campaignSummaryContainer: {
    marginTop: '12px',
    marginBottom: '8px',
  },

  campaignSummaryLabel: {
    fontSize: '0.8rem',
    color: claymorphismColors.textLight,
    fontWeight: 500,
    marginBottom: '8px',
  },

  campaignChipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },

  campaignChip: {
    backgroundColor: 'rgba(232, 93, 4, 0.15)',
    color: claymorphismColors.accent,
    fontWeight: 500,
    fontSize: '0.8rem',
    maxWidth: '180px',
  },
  campaignChipMore: {
    backgroundColor: 'rgba(0, 0, 0, 0.08)',
    color: claymorphismColors.textLight,
    fontWeight: 600,
    fontSize: '0.8rem',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },

  expandButton: {
    padding: '4px',
    transition: 'transform 0.3s ease-out',
    color: claymorphismColors.textLight,
  },

  expandButtonOpen: {
    transform: 'rotate(180deg)',
  },

  expandSection: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    borderTop: '1px solid rgba(0, 0, 0, 0.08)',
    paddingTop: '8px',
    marginTop: '8px',
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover $expandLabel': {
      color: claymorphismColors.accent,
    },
  },

  expandLabel: {
    fontSize: '0.8rem',
    color: claymorphismColors.textLight,
    marginRight: '4px',
    transition: 'color 0.2s ease-out',
  },

  recordList: {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
    borderRadius: '8px',
    padding: '12px',
    marginTop: '12px',
  },

  recordItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '8px 0',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    '&:last-child': {
      borderBottom: 'none',
    },
  },

  recordCampaign: {
    flex: 1,
    fontSize: '0.85rem',
    color: claymorphismColors.text,
    fontWeight: 500,
    wordBreak: 'break-word',
  },

  recordTime: {
    flexShrink: 0,
    fontSize: '0.8rem',
    color: claymorphismColors.textLight,
    whiteSpace: 'nowrap',
    textAlign: 'right',
  },

  // Desktop grid layout for cards
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
}));

export default useStyles;
